import { useEffect, useRef } from 'react';
import { marketDataWS } from '../../../api/marketDataWebSocket';

/**
 * Hook to declaratively subscribe to instrument tokens for real-time market data.
 * Automatically handles subscribing on mount/update and unsubscribing on unmount/update.
 * 
 * @param tokens Array of broker_instrument_token strings
 */
export function useMarketDataSubscription(tokens: string[]) {
    // Keep track of what we are currently subscribed to
    // so we can figure out what to unsubscribe from when the array changes
    const subscribedTokensRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        // Determine new tokens to subscribe to
        const newTokens = tokens.filter(t => !subscribedTokensRef.current.has(t));

        // Determine old tokens to unsubscribe from
        // (if a token is in ref but no longer in the passed tokens array)
        const tokenSet = new Set(tokens);
        const staleTokens = Array.from(subscribedTokensRef.current).filter(t => !tokenSet.has(t));

        if (newTokens.length > 0) {
            marketDataWS.subscribe(newTokens);
            newTokens.forEach(t => subscribedTokensRef.current.add(t));
        }

        if (staleTokens.length > 0) {
            marketDataWS.unsubscribe(staleTokens);
            staleTokens.forEach(t => subscribedTokensRef.current.delete(t));
        }

    }, [tokens]); // Re-run whenever the tokens array physically changes

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            const allTokens = Array.from(subscribedTokensRef.current);
            if (allTokens.length > 0) {
                marketDataWS.unsubscribe(allTokens);
                subscribedTokensRef.current.clear();
            }
        };
    }, []);
}
